import React, { FC, useState, useEffect } from 'react';
import { StarsRatingBarProps } from '@wix/design-system/dist/types/StarsRatingBar';
import { StatusIndicatorState } from '@wix/design-system/dist/types/StatusIndicator';
import { useErrorMonitor } from '@wix/fe-essentials-standalone';
import {
  MODAL_ACTIONS,
  useModalsContext,
} from '@components/Contexts/ModalsContext';
import {
  Modal,
  CustomModalLayout,
  Box,
  Loader,
  Heading,
  starRatingBarValue,
} from '@wix/design-system';
import { ReviewData } from '@common/types';

import StarRating from '@components/Reviews/ReviewModal/StarRating';
import ReviewTextArea from '@components/Reviews/ReviewModal/ReviewTextArea';
import MobileModalContent from '@components/Reviews/ReviewModal/ModalContent/MobileModalContent';

export type ModalContentProps = {
  onClose?: () => void;
  onCancel?: () => void;
  onRateChange?: (rate: number) => void;
  isMobileView?: boolean;
  title: string;
  subtitle?: string;
  mainCtaText: string;
  secondaryCtaText: string;
  errorMsg: string;
  textAreaLabel: string;
  textAreaPlaceholder?: string;
  publish: any;
  ProviderDetailsComponent?: any;
  TemplateDetailsComponent?: any;
  ratingScore: StarsRatingBarProps['value'];
  setRatingScore: (score: StarsRatingBarProps['value']) => void;
  enableAutoFocus?: boolean;
  userReview?: ReviewData | null;
};

const ModalContent: FC<ModalContentProps> = ({
  onClose,
  onCancel,
  onRateChange,
  isMobileView = false,
  title,
  subtitle,
  mainCtaText,
  secondaryCtaText,
  errorMsg,
  textAreaLabel,
  textAreaPlaceholder,
  publish,
  ProviderDetailsComponent,
  TemplateDetailsComponent,
  ratingScore,
  setRatingScore,
  enableAutoFocus,
  userReview,
}) => {
  const { dispatchModal, showReviewModal } = useModalsContext();
  const errorMonitor = useErrorMonitor();
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [reviewText, setReviewText] = useState<string>('');
  const [reviewTextStatus, setReviewTextError] = useState<
    StatusIndicatorState | undefined
  >(undefined);
  const [reviewScoreStatus, setReviewScoreError] = useState<
    StatusIndicatorState | undefined
  >(undefined);
  const [isReviewTextChanged, setIsReviewTextChanged] =
    useState<boolean>(false);
  const [isReviewScoreChanged, setIsReviewScoreChanged] =
    useState<boolean>(false);

  const isReviewScoreValid = ratingScore > 0;
  const isReviewTextValid = reviewText.trim() !== '';

  const resetModalState = () => {
    setRatingScore(0);
    setReviewText('');
    setReviewTextError(undefined);
    setReviewScoreError(undefined);
    setIsReviewTextChanged(false);
    setIsReviewScoreChanged(false);
  };

  useEffect(() => {
    setReviewText('');
    if (!!userReview) {
      setRatingScore(userReview.overallScore as starRatingBarValue);
      setReviewText(userReview.text || '');
    }
  }, [userReview]);

  function onCancelClick() {
    onCancel && onCancel();
    closeModal();
  }

  function onCloseClick() {
    onClose && onClose();
    closeModal();
  }

  function closeModal() {
    dispatchModal({
      type: MODAL_ACTIONS.HIDE_REVIEW_MODAL,
    });
    resetModalState();
  }

  async function submitReview() {
    if (!inProgress) {
      setInProgress(true);
      setReviewTextError(!isReviewTextValid ? 'error' : undefined);
      setReviewScoreError(!isReviewScoreValid ? 'error' : undefined);
      setIsReviewScoreChanged(true);
      if (isReviewTextValid && isReviewScoreValid) {
        try {
          await publish(reviewText);
          setInProgress(false);
          closeModal();
        } catch (e) {
          setInProgress(false);
          closeModal();
          dispatchModal({
            type: MODAL_ACTIONS.SHOW_ERROR_TOAST,
            message: errorMsg,
          });
          errorMonitor.captureException(e as Error);
        }
      } else {
        setInProgress(false);
      }
    }
  }

  const primaryButtonText = inProgress ? (
    <Loader size="tiny" color="white" />
  ) : (
    mainCtaText
  );

  return (
    <Modal
      isOpen={showReviewModal}
      dataHook="review-modal"
      screen={isMobileView ? 'full' : 'desktop'}
    >
      {isMobileView ? (
        <MobileModalContent
          title={title}
          textAreaLabel={textAreaLabel}
          textAreaPlaceholder={textAreaPlaceholder}
          TemplateDetailsComponent={TemplateDetailsComponent}
          ratingScore={ratingScore}
          setRatingScore={setRatingScore}
          onCancel={onCancelClick}
          onClose={onCloseClick}
          submitReview={submitReview}
          primaryButtonText={primaryButtonText}
          reviewScoreStatus={reviewScoreStatus}
          isReviewScoreChanged={isReviewScoreChanged}
          setReviewScoreError={setReviewScoreError}
          setIsReviewScoreChanged={setIsReviewScoreChanged}
          reviewText={reviewText}
          reviewTextStatus={reviewTextStatus}
          isReviewTextChanged={isReviewTextChanged}
          setReviewText={setReviewText}
          setReviewTextError={setReviewTextError}
          setIsReviewTextChanged={setIsReviewTextChanged}
          enableAutoFocus={enableAutoFocus}
          onRateChange={onRateChange}
        />
      ) : (
        <CustomModalLayout
          dataHook="review-modal-layout"
          width={ProviderDetailsComponent ? '803px' : '600px'}
          secondaryButtonText={secondaryCtaText}
          onCloseButtonClick={onCloseClick}
          secondaryButtonOnClick={onCancelClick}
          primaryButtonOnClick={submitReview}
          primaryButtonProps={{ disabled: inProgress }}
          showFooterDivider={false}
          subtitle={subtitle ? subtitle : null}
          title={
            <Box dataHook="baseModalLayout-title">
              <Heading size="large">{title}</Heading>
            </Box>
          }
          primaryButtonText={primaryButtonText}
          content={
            <Box padding="6px 0">
              <Box direction="vertical" gap="30px">
                {TemplateDetailsComponent && TemplateDetailsComponent}
                <StarRating
                  overallScore={ratingScore}
                  reviewScoreStatus={reviewScoreStatus}
                  isReviewScoreChanged={isReviewScoreChanged}
                  setReviewScore={setRatingScore}
                  setReviewScoreError={setReviewScoreError}
                  setIsReviewScoreChanged={setIsReviewScoreChanged}
                  onRateChange={onRateChange}
                />
                <ReviewTextArea
                  textLabel={textAreaLabel}
                  textPlaceholder={textAreaPlaceholder}
                  reviewText={reviewText}
                  reviewTextStatus={reviewTextStatus}
                  isReviewTextChanged={isReviewTextChanged}
                  setReviewText={setReviewText}
                  setReviewTextError={setReviewTextError}
                  setIsReviewTextChanged={setIsReviewTextChanged}
                />
              </Box>
              {ProviderDetailsComponent && !isMobileView ? (
                <Box
                  backgroundColor="#F4F4F4"
                  borderRadius="8px"
                  width="232px"
                  marginLeft="SP4"
                >
                  {ProviderDetailsComponent}
                </Box>
              ) : null}
            </Box>
          }
        />
      )}
    </Modal>
  );
};

export default ModalContent;
